import { useEffect, useRef, useState, type JSX } from 'react'
import { Loader2, User, Users } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { usePrincipals } from '../api/use-principals'

export interface PrincipalSelection {
  principalType: 'user' | 'team'
  principalId: string
  label: string
}

interface PrincipalPickerProps {
  onSelect: (principal: PrincipalSelection) => void
}

const DEBOUNCE_MS = 250

/**
 * Debounced typeahead over the tenant-scoped principal search endpoint
 * (GET /collaboration/principals). Lets the caller (ShareDialog) pick a
 * single user or team to grant access to.
 */
export function PrincipalPicker({ onSelect }: PrincipalPickerProps): JSX.Element {
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect((): (() => void) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    debounceRef.current = setTimeout((): void => {
      setDebouncedQuery(query)
    }, DEBOUNCE_MS)
    return (): void => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [query])

  const { data, isLoading, isFetching } = usePrincipals(debouncedQuery)

  const trimmedQuery = query.trim()
  // Panel visibility follows the live query (so it opens as soon as the user
  // types 2 chars), but result/loading gating follows debouncedQuery — the
  // query that actually drives the fetch — so we never flash "No results"
  // for the still-pending previous debounce cycle.
  const isSearchable = trimmedQuery.length >= 2
  const isDebouncePending = query !== debouncedQuery
  const users = data?.users ?? []
  const teams = data?.teams ?? []
  const hasResults = users.length > 0 || teams.length > 0
  const showLoading = isSearchable && (isDebouncePending || isLoading || isFetching)

  return (
    <div className="space-y-2">
      <Input
        type="text"
        placeholder="Search people or teams..."
        value={query}
        onChange={(e): void => setQuery(e.target.value)}
        aria-label="Search people or teams"
      />

      {isSearchable && (
        <ScrollArea className="max-h-64 rounded-md border">
          <div className="p-1">
            {showLoading ? (
              <div className="flex items-center gap-2 px-2 py-3 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Searching...
              </div>
            ) : hasResults ? (
              <>
                {users.map((user): JSX.Element => (
                  <button
                    key={`user-${user.id}`}
                    type="button"
                    onClick={(): void =>
                      onSelect({ principalType: 'user', principalId: user.id, label: user.name })
                    }
                    className="flex w-full items-center gap-2 rounded-sm px-2 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground"
                  >
                    <User className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="flex flex-col overflow-hidden">
                      <span className="truncate font-medium">{user.name}</span>
                      <span className="truncate text-xs text-muted-foreground">{user.email}</span>
                    </span>
                  </button>
                ))}
                {teams.map((team): JSX.Element => (
                  <button
                    key={`team-${team.id}`}
                    type="button"
                    onClick={(): void =>
                      onSelect({ principalType: 'team', principalId: team.id, label: team.name })
                    }
                    className="flex w-full items-center gap-2 rounded-sm px-2 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground"
                  >
                    <Users className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="flex flex-col overflow-hidden">
                      <span className="truncate font-medium">{team.name}</span>
                      <span className="truncate text-xs text-muted-foreground">
                        {team.memberCount} member{team.memberCount === 1 ? '' : 's'}
                      </span>
                    </span>
                  </button>
                ))}
              </>
            ) : (
              <p className="px-2 py-3 text-sm text-muted-foreground">No people or teams found.</p>
            )}
          </div>
        </ScrollArea>
      )}
    </div>
  )
}
